import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Button } from "primereact/button";

interface selectedOrderDataProps {
  orderinfo: any;
}

const OrderPrint: React.FC<selectedOrderDataProps> = ({ orderinfo }) => {
  const orderRef = useRef(null);

  const handleOrderPrint = useReactToPrint({
    content: () => orderRef.current,
    documentTitle: 'Order Details',
    pageStyle: `@page { margin-top: 5mm; margin-left: 5mm; margin-right: 3mm; margin-bottom: 1mm; }`
  });

  if (!orderinfo) {
    return null;
  }

  const totalPaymentGatewayCharges = orderinfo.invoices?.reduce(
    (total: number, invoice: any) => 0 + (invoice.invoicePaymentGatewayCharges || 0),
    0
  ) || 0;


  const formatAddressLine = (parts: (string | undefined)[], separator = ', ') => {
    const validParts = parts.filter(part => part && part.trim() !== '');
    return validParts.join(separator);
  };

  const renderDeliveryAddress = () => {
    const addressParts = [
      orderinfo.address1,
      orderinfo.address2,
      orderinfo.landmark,
      orderinfo.areaName,
      orderinfo.subDistrictName,
      orderinfo.cityName,
      orderinfo.stateName,
      orderinfo.countryName
    ].filter(Boolean);

    return (
      <div className="col">
        <span className="text-primary">
          {formatAddressLine(addressParts)}
        </span><br />
        {orderinfo.zipcode && `Pin: ${orderinfo.zipcode}`}
      </div>
    );
  };

  const productImageTemplate = (product: any) => {
    return (
      <img 
        src={product.productImageURL} 
        alt={product.productName} 
        width="50" 
        height="50" 
        style={{ objectFit: 'cover' }}
      />
    );
  };

  const priceBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(product.unitPrice);
  };

  const quantityBodyTemplate = (product: any) => {
    return `${product.quantity} ${product.measurementTypeName || product.measurementTypeCode || ''}`;
  };

  const amountBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(product.amount || (product.unitPrice * product.quantity));
  };

  const taxBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(product.tax);
  };

  const totalAmountBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(Math.round(
      (product.amount || (product.unitPrice * product.quantity)) + (product.tax || 0))
    );
  };

  const expectedDeliveryTemplate = (product: any) => {
    return product.expectedDeliveryDate ? 
      new Date(product.expectedDeliveryDate).toLocaleDateString("en-gb", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      }) : 
      'N/A';
  };

  const headerTemplate = (
    <>
      <div className="grid">
        <div className="col-6">
          <div className="grid mb-3">
            <img src={`${process.env.PUBLIC_URL}/assests/images/DesiriLogo.png`} width={100} alt="Desiri Logo" />
          </div>
          {orderinfo.invoices?.[0]?.poS_Address1 && (
            <div>
              <div className="grid">
                <div className="col">From:<br /> <span className="text-primary">Desiri Naturals Pvt Ltd</span></div>
              </div>
              <div className="grid">
                <div className="col">
                  <span className="text-primary">
                    {[
                      orderinfo.invoices[0]?.poS_Address1,
                      orderinfo.invoices[0]?.poS_Address2,
                      orderinfo.invoices[0]?.poS_CityName,
                      orderinfo.invoices[0]?.poS_LandMark,
                      orderinfo.invoices[0]?.poS_StateName,
                      orderinfo.invoices[0]?.poS_CountryName,
                    ].filter(Boolean).join(', ')}
                  </span><br />
                  {`Pin: ${orderinfo.invoices[0]?.poS_Zipcode}`}
                </div>
              </div>
              <div className="grid">
                <div className="col">
                  GST #: <span className="text-primary">29AAGCD5864J1Z4</span>
                </div>
              </div>
            </div>
            )}
          <div className="grid">
            <div className="col">To:<br /> <span className="text-primary">{orderinfo.userName}</span></div>
          </div>
          <div className="grid">
            <div className="col">Delivery Address:<br />
              {renderDeliveryAddress()}
            </div>
          </div>
          <div className="grid">
            <div className="col">Mobile #: <span className="text-primary">{orderinfo.primaryContactNbr}</span></div>
          </div>
          {orderinfo.gsT_Number && (
            <div className="grid">
              <div className="col">GST #: <span className="text-primary">{orderinfo.gsT_Number}</span></div>
            </div>
          )}
        </div>
        <div className="col-1"></div>
        <div className="col-5">
          <div className="grid mt-7">
            <div className="col">Order #: <span className="text-primary">{orderinfo.orderID}</span></div>
          </div>
          <div className="grid">
            <div className="col">Order Date: <span className="text-primary">
              {new Date(orderinfo.orderDate).toLocaleDateString("en-gb", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })}
            </span></div>
          </div>
          <div className="grid">
            <div className="col">Order Amount: <span className="text-primary">
              {Helpers.formatAmountInINR(orderinfo.total)}
            </span></div>
          </div>
          <div className="grid">
            <div className="col">Payment Mode: <span className="text-primary">{orderinfo.paymentMode}</span></div>
          </div>
        </div>
      </div>
    </>
  );

  const footerTemplate = (
    <>
      <div className="grid">
        <div className="col-7"></div>
        <div className="col-5">
          <div className="grid">
            <div className="col">Taxable Amount:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.totalCost)}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col">Tax Amount:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.taxAmount)}</div>
            </div>
          </div>
          {
            orderinfo.courierCharges > 0 ? 
            <div className="grid">
            <div className="col">Courier Charges:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.courierCharges)}</div>
            </div>
          </div> : ""
          }
          { totalPaymentGatewayCharges > 0 ?
            <div className="grid">
            <div className="col">Payment Gateway Charges:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(totalPaymentGatewayCharges)}</div>
            </div>
          </div> : ""
          }
          {orderinfo.discountAmount > 0 && (
            <div className="grid">
              <div className="col">Discount Amount:
                <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(orderinfo.discountAmount)}</div>
              </div>
            </div>
          )}
          <div className="grid">
            <div className="col">Total Amount:
              <div className="mr-2 text-primary" style={{ float: "right" }}>
                {Helpers.formatAmountInINR(orderinfo.discountAmount > 0 ? Math.round(orderinfo.total - orderinfo.discountAmount) : Math.round(orderinfo.total))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Button 
        icon="pi pi-print" 
        style={{ float: "right", marginRight: 2 }} 
        onClick={handleOrderPrint} 
        rounded 
        text 
        severity="info" 
        aria-label="Print" 
      />
      <div ref={orderRef} style={{ overflowX: 'hidden' }}>
        <DataTable
          value={orderinfo.products || []}
          className="no-flex"
          header={headerTemplate}
          footer={footerTemplate}
          size="small"
        >
          <Column header="Image" body={productImageTemplate} style={{ width: '80px' }} />
          <Column field="productName" header="Product Name" />
          <Column header="Unit Price" body={priceBodyTemplate} />
          <Column header="Quantity" body={quantityBodyTemplate} />
          <Column header="Amount" body={amountBodyTemplate} />
          <Column header="Tax" body={taxBodyTemplate} />
          <Column header="Total" body={totalAmountBodyTemplate} />
          <Column header="Expected Delivery" body={expectedDeliveryTemplate} />
        </DataTable>
      </div>
    </>
  );
};

export default OrderPrint;